import { z } from 'zod'
import { toast } from 'sonner'
import { useState } from 'react'
import { paths } from '@/routes/paths'
import { useForm } from 'react-hook-form'
import { uploadFile } from '@/lib/firebase'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { fError } from '@/utils/format-error'
import { Store, Image, X } from 'lucide-react'
import axios, { endpoints } from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { useBoolean } from '@/hooks/use-boolean'
import { useRouter } from '@/routes/hooks/use-router'
import { zodResolver } from '@hookform/resolvers/zod'
import RichTextEditor from '@/components/ui/rich-text-editor'
import { type User, type ShopStatus, AuthProvider } from '@/types/user.type'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

interface ShopImages {
  logo: { url: File | string; fileName: string } | null
  cover: { url: File | string; fileName: string } | null
  banner: { url: File | string; fileName: string } | null
}

interface CreateShopData {
  email: string
  password: string
  displayName: string
  imageUrl?: string
  shopCoverUrl?: string
  shopBannerUrl?: string
  shopDescription?: string
  lineGroupId?: string
  shopStatus: ShopStatus
}

// Schema definition
const createShopFormSchema = z.object({
  email: z.string().email('กรุณาป้อนอีเมลที่ถูกต้อง'),
  password: z.string().min(6, 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร'),
  displayName: z.string().min(1, 'กรุณาป้อนชื่อเจ้าของร้าน'),
  description: z.string().optional(),
  lineGroupId: z.string().optional(),
  shopStatus: z.enum(['OPEN', 'CLOSED']),
})

const editShopFormSchema = z.object({
  email: z.string().email('กรุณาป้อนอีเมลที่ถูกต้อง'),
  password: z.string().optional(),
  displayName: z.string().min(1, 'กรุณาป้อนชื่อเจ้าของร้าน'),
  description: z.string().optional(),
  lineGroupId: z.string().optional(),
  shopStatus: z.enum(['OPEN', 'CLOSED']),
})

type CreateFormData = z.infer<typeof createShopFormSchema>
type EditFormData = z.infer<typeof editShopFormSchema>
type FormData = CreateFormData | EditFormData

interface ShopFormProps {
  shop?: User // For edit mode
  mode: 'create' | 'edit'
}

export default function ShopForm({ shop, mode }: ShopFormProps) {
  const router = useRouter()
  const submitting = useBoolean(false)
  const [images, setImages] = useState<ShopImages>({
    logo: shop?.imageUrl ? { url: shop.imageUrl, fileName: 'logo' } : null,
    cover: shop?.shopCoverUrl
      ? { url: shop.shopCoverUrl, fileName: 'cover' }
      : null,
    banner: shop?.shopBannerUrl
      ? { url: shop.shopBannerUrl, fileName: 'banner' }
      : null,
  })

  const form = useForm<FormData>({
    resolver: zodResolver(
      mode === 'create' ? createShopFormSchema : editShopFormSchema,
    ),
    defaultValues: {
      email: shop?.email || '',
      password: '', // Don't populate password for security
      displayName: shop?.displayName || '',
      description: shop?.shopDescription || '',
      lineGroupId: shop?.lineGroupId || '',
      shopStatus: shop?.shopStatus || 'CLOSED',
    },
  })

  const handleImageChange = (type: keyof ShopImages, file: File | null) => {
    if (file) {
      setImages({
        ...images,
        [type]: {
          url: file,
          fileName: file.name,
        },
      })
    } else {
      setImages({
        ...images,
        [type]: null,
      })
    }
  }

  const onSubmit = async (data: FormData) => {
    try {
      submitting.onTrue()

      // Upload images
      const uploadedImages: { [key: string]: string } = {}

      for (const [type, image] of Object.entries(images)) {
        if (image) {
          if (typeof image.url === 'string') {
            // Existing image URL
            if (type === 'logo') {
              uploadedImages['imageUrl'] = image.url
            } else if (type === 'cover') {
              uploadedImages['shopCoverUrl'] = image.url
            } else if (type === 'banner') {
              uploadedImages['shopBannerUrl'] = image.url
            }
          } else {
            // New image file
            const url = await uploadFile(
              image.url,
              `shops/${type}s`,
              image.fileName,
            )
            if (type === 'logo') {
              uploadedImages['imageUrl'] = url
            } else if (type === 'cover') {
              uploadedImages['shopCoverUrl'] = url
            } else if (type === 'banner') {
              uploadedImages['shopBannerUrl'] = url
            }
          }
        }
      }

      // Prepare shop data
      const shopData = {
        email: data.email,
        displayName: data.displayName,
        shopDescription: data.description,
        lineGroupId: data.lineGroupId,
        shopStatus: data.shopStatus as ShopStatus,
        authProvider: AuthProvider.EMAIL,
        ...uploadedImages,
      }

      if (mode === 'edit' && shop) {
        // Update existing shop - exclude email and password
        const updateData = {
          displayName: shopData.displayName,
          shopDescription: shopData.shopDescription,
          lineGroupId: shopData.lineGroupId,
          shopStatus: shopData.shopStatus,
          ...uploadedImages,
        }
        await axios.patch(endpoints.users.update(shop.uid), updateData)
        toast.success('แก้ไขร้านค้าสำเร็จ')
      } else {
        // Create new shop
        const createData: CreateShopData = {
          ...shopData,
          password: (data as CreateFormData).password, // User must provide password
        }
        await axios.post(endpoints.users.create, createData)
        toast.success('สร้างร้านค้าสำเร็จ')
      }

      router.push(paths.shops)
    } catch (error) {
      console.error(error)
      toast.error(fError(error))
    } finally {
      submitting.onFalse()
    }
  }

  const handleBack = () => {
    router.push(paths.shops)
  }

  const renderImageUpload = (
    type: keyof ShopImages,
    label: string,
    previewRatio: string,
  ) => {
    const image = images[type]

    return (
      <div className="space-y-2">
        <Label className="text-base font-medium text-black">{label}</Label>
        <div className="space-y-2">
          <Input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0] || null
              handleImageChange(type, file)
            }}
            className="rounded-lg border"
          />
          {image && (
            <div
              className={`relative w-full max-w-80 rounded-lg overflow-hidden bg-gray-100 border ${previewRatio}`}
            >
              {typeof image.url === 'string' ? (
                <img
                  src={image.url}
                  alt={label}
                  className="w-full h-full object-cover"
                />
              ) : (
                <img
                  src={URL.createObjectURL(image.url)}
                  alt={label}
                  className="w-full h-full object-cover"
                />
              )}
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => handleImageChange(type, null)}
                className="absolute top-1 right-1 h-6 w-6 p-0"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <Form {...form}>
      <form
        id="shop-form"
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-6"
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="border rounded-lg">
              <CardHeader>
                <CardTitle className="text-base font-semibold text-black flex items-center gap-2">
                  <Store className="h-5 w-5" />
                  ข้อมูลร้านค้า
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 px-4">
                {/* Shop Name */}
                <FormField
                  control={form.control}
                  name="displayName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-medium text-black">
                        ชื่อร้าน *
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="กรอกชื่อร้าน"
                          className="rounded-lg border"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Description */}
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-medium text-black">
                        รายละเอียดร้าน
                      </FormLabel>
                      <FormControl>
                        <RichTextEditor
                          value={field.value || ''}
                          onChange={field.onChange}
                          placeholder="กรอกรายละเอียดร้าน"
                          className="min-h-[200px]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Line Group ID */}
                <FormField
                  control={form.control}
                  name="lineGroupId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-medium text-black">
                        Line Group ID
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="กรอก Line Group ID"
                          className="rounded-lg border"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Images */}
                <div className="space-y-4">
                  <Label className="text-base font-medium text-black flex items-center gap-2">
                    <Image className="h-4 w-4" />
                    รูปภาพร้าน
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderImageUpload('logo', 'โลโก้ร้าน', 'aspect-[1/1]')}
                    {renderImageUpload('cover', 'รูปปก', 'aspect-[2.5/1]')}
                    {/* {renderImageUpload('banner', 'แบนเนอร์')} */}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="border rounded-lg">
              <CardHeader>
                <CardTitle className="text-base font-semibold text-black">
                  ข้อมูลผู้ใช้
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Email */}
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-medium text-black">
                        อีเมล *
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          placeholder="กรอกอีเมล"
                          className="rounded-lg border"
                          disabled={mode === 'edit'}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Password - Only show in create mode */}
                {mode === 'create' && (
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base font-medium text-black">
                          รหัสผ่าน *
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="password"
                            placeholder="กรอกรหัสผ่าน (จำเป็น)"
                            className="rounded-lg border"
                            autoComplete="new-password"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Status */}
                <FormField
                  control={form.control}
                  name="shopStatus"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-medium text-black">
                        สถานะ
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="rounded-lg border">
                            <SelectValue placeholder="เลือกสถานะ" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="CLOSED">ปิดร้าน</SelectItem>
                          <SelectItem value="OPEN">เปิดร้าน</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex gap-3 py-6 border-t">
          <Button type="submit" form="shop-form" disabled={submitting.value}>
            {submitting.value
              ? 'กำลังบันทึก...'
              : mode === 'edit'
                ? 'แก้ไข'
                : 'สร้าง'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            disabled={submitting.value}
          >
            ยกเลิก
          </Button>
        </div>
      </form>
    </Form>
  )
}
